<template>
  <WizardContainer
    width="792px"
    height="624px"
  >
    <WizardContent>
      <h4 class="mt-6 mb-8">
        {{ heading }}
      </h4>
      <p class="p3 mb-9">
        {{ subheading }}
      </p>
      <v-form
        ref="form"
        v-model="valid"
        class="form"
        @submit.prevent="submitForm"
      >
        <DpplTextField
          v-model="password"
          label="Password"
          :rules="passwordRules"
          type="password"
          autocomplete="off"
          validate-on="blur"
          data-sentry-mask
        />
        <DpplTextField
          v-model="confirmPassword"
          class="mb-3"
          label="Confirm Password"
          :rules="confirmPasswordRules"
          type="password"
          autocomplete="off"
          validate-on="blur"
          data-sentry-mask
        />
        <DpplSignupFormButton
          type="submit"
          block
          :loading="loading"
        >
          Submit
        </DpplSignupFormButton>
      </v-form>
    </WizardContent>
  </WizardContainer>
</template>

<script>
import WizardContainer from '@/components/shared/WizardContainer.vue';
import WizardContent from '@/components/shared/WizardContent.vue';
import { signupSetPassword } from '@/api/signup';
import validation from '@/helpers/validation';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSignupFormButton from '@/components/datapeople/DpplSignupFormButton.vue';
import { mapActions } from 'pinia';
import { useWizard } from '@/stores/wizard/wizard';

export default {
  name: 'CreatePassword',
  components: {
    WizardContainer,
    WizardContent,
    DpplTextField,
    DpplSignupFormButton,
  },
  emits: ['track-page-load'],
  data() {
    return {
      reason: this.$route.query.reason || null,
      passwordResetToken: this.$route.query.token || null,
      email: this.$route.query.email || '',
      password: '',
      confirmPassword: '',
      passwordRules: validation.passwordRules(),
      confirmPasswordRules: [(v) => v === this.password || 'Passwords must match'],
      valid: false,
      loading: false,
      firstName: this.$route.query.firstName || 'Friend',
    };
  },
  computed: {
    heading() {
      if (this.reason === 'expired') {
        return 'Your Datapeople password has expired.';
      }
      return 'Create a Password';
    },
    subheading() {
      if (this.reason === 'expired') {
        return 'Please create a new password.';
      }
      return `Hey ${this.firstName}, it looks like your Datapeople account needs a new password.`
        + ' Please set one below.';
    },
  },
  async created() {
    if (!this.passwordResetToken) {
      await this.$router.push({
        name: 'signup',
      });
    }
  },
  beforeCreate() {
    this.$emit('track-page-load', { page: 'Create Password' });
  },
  methods: {
    ...mapActions(useWizard, ['updateFlash']),
    async submitForm() {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await signupSetPassword({
            passwordResetToken: this.passwordResetToken,
            email: this.email,
            password: this.password,
            confirmPassword: this.confirmPassword,
          });
          if (responseJSON.success === true) {
            this.$router.replace({ name: 'login', query: { passwordChanged: 'true' } });
          }
        }
      } catch (e) {
        if (!e.message || e.message.charAt(0) !== '{') {
          this.updateFlash({ message: `Create password error: ${e.message}` });
          throw e;
        }
        const responseJSON = JSON.parse(e.message);
        if (responseJSON.errors && responseJSON.errors.sso_mandatory) {
          if (responseJSON.errors.is_google) {
            window.location.href = '/signup/google';
          } else if (responseJSON.errors.is_microsoft) {
            window.location.href = '/signup/microsoft';
          } else {
            window.location.href = `/login/${responseJSON.errors.provider}`;
          }
          return;
        }
        if (
          responseJSON.errors
          && responseJSON.errors.password
          && responseJSON.errors.password
            .includes('New password can not be one of your last 10 passwords.')
        ) {
          this.updateFlash({ message: 'New password can not be one of your last 10 passwords.' });
        } else if (
          responseJSON.errors
          && responseJSON.errors.password_reset_token
          && responseJSON.errors.password_reset_token.includes('Invalid Token')
        ) {
          this.updateFlash({ message: 'Password reset link expired. Please request a new password reset link' });
        } else {
          this.updateFlash({ message: 'Something went wrong' });
        }
        throw e;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.form {
  width: 100%;
}
</style>
